import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Icons } from '../utils/icons';
import api from '../utils/api';
import { fontStack, btnSecondary, btnSmall } from '../styles/shared';
import CustomSelect from './CustomSelect';
import PayoutSection from './PayoutSection';
import { useToast } from '../contexts/ToastContext';

// ─── Constants ───────────────────────────────────────────────────────────────
const ITEMS_PER_PAGE = 15;
const CHART_PERIODS = [
    { value: 'today', label: 'Today', days: 0 },
    { value: 'yesterday', label: 'Yesterday', days: 1 },
    { value: '7d', label: 'Last 7 Days', days: 7 },
    { value: '30d', label: 'Last 30 Days', days: 30 },
    { value: '90d', label: 'Last 90 Days', days: 90 },
    { value: '365d', label: 'Last 365 Days', days: 365 },
];
const TYPE_MAP = { 'All': null, 'Subscriptions': 'subscription', 'Tips': 'tip', 'Messages': 'ppv', 'Spins': 'spin', 'Chargebacks': 'chargeback', 'Trials': 'trial', 'Referrals': 'referral' };

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtUsd = (v) => `$${(v || 0).toFixed(2)}`;
const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtDateTime = (d) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const fmtFull = (d) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const purchaseLabel = (t) => {
    if (t === 'subscription') return 'Subscription';
    if (t === 'ppv') return 'Message';
    if (t === 'tip') return 'Tip';
    if (t === 'chargeback') return 'Chargeback';
    if (t === 'refund') return 'Refund';
    if (t === 'trial') return 'Trial';
    if (t === 'referral') return 'Referral Payout';
    return 'Spin Purchase';
};
const purchaseIcon = (t) => {
    if (t === 'subscription') return <Icons.Users />;
    if (t === 'ppv') return <Icons.MessageCircle />;
    if (t === 'tip') return <Icons.DollarSign />;
    if (t === 'chargeback' || t === 'refund') return <Icons.AlertTriangle />;
    if (t === 'trial') return <Icons.Users />;
    if (t === 'referral') return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
    );
    return <Icons.Gift />;
};
const purchaseColor = (t) => {
    if (t === 'subscription') return '#10b981';
    if (t === 'ppv') return '#f59e0b';
    if (t === 'tip') return '#8b5cf6';
    if (t === 'chargeback' || t === 'refund') return '#ef4444';
    if (t === 'trial') return '#9ca3af';
    if (t === 'referral') return '#a855f7';
    return '#00AEEF';
};

// ─── Component ───────────────────────────────────────────────────────────────
export default function PaymentsModal({ onClose, wheels = [], isChatter = false, adminViewChatter = null, fullPage = false }) {
    const toast = useToast();
    const location = useLocation();
    const autoVerify = new URLSearchParams(location.search).get('verify') === '1';

    // Data state
    const [earnings, setEarnings] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [dailyEarnings, setDailyEarnings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setCryptoEarnings] = useState(null);
    const [cryptoBreakdown, setCryptoBreakdown] = useState(null);
    const [channelBreakdown, setChannelBreakdown] = useState([]);
    const [subscriptionEarnings, setSubscriptionEarnings] = useState(null);
    const [totalEarnedAllUsd, setTotalEarnedAllUsd] = useState(null);

    // Config
    const [minPayout, setMinPayout] = useState(1000);
    const [starsPerUsd, setStarsPerUsd] = useState(77);
    const [holdingPeriodDays, setHoldingPeriodDays] = useState(21);
    const [usdHoldingPeriodDays, setUsdHoldingPeriodDays] = useState(14);
    const [adminSharePct, setAdminSharePct] = useState(0.85);
    const [transferMode, setTransferMode] = useState('');


    // UI state
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [requesting, setRequesting] = useState(false);
    const [usdtWallet, setUsdtWallet] = useState('');
    const [savingWallet, setSavingWallet] = useState(false);
    const [chartTooltip, setChartTooltip] = useState(null);
    const [chartPeriod, setChartPeriod] = useState('30d');
    const [txSearch, setTxSearch] = useState('');
    const [txFilterType, setTxFilterType] = useState('All');
    const [txFilterChannel, setTxFilterChannel] = useState('All');
    const [txPage, setTxPage] = useState(1);
    const [detailTx, setDetailTx] = useState(null);
    const [usdPayoutAmount, setUsdPayoutAmount] = useState('');
    const [showWithdrawSection, setShowWithdrawSection] = useState(autoVerify);

    const chartContainerRef = useRef(null);
    const scrollRef = useRef(null);
    const seenTxIds = useRef(new Set());

    // ─── Data Loading ────────────────────────────────────────────────────────
    useEffect(() => {
        if (adminViewChatter) {
            loadChatterEarnings();
        } else if (isChatter) {
            loadChatterSelfEarnings();
        } else {
            loadEarnings();
            loadWallet();
        }
    }, []);


    const loadChatterEarnings = async () => {
        try {
            const res = await api.get(`/api/admin/chatters/earnings?chatter_id=${adminViewChatter.id}`);
            const d = res.data;
            setEarnings({ total_earned: d.stars_total, available_balance: 0, pending_balance: 0, total_withdrawn: 0 });
            setTransactions(d.transactions || []);
            setCryptoEarnings({ admin_share_usd: d.crypto_admin_share_usd || 0 });
            setStarsPerUsd(d.stars_per_usd || 77);
            setAdminSharePct((d.admin_share_percent ?? 80) / 100);
            setDailyEarnings(d.daily_earnings || []);
            setChannelBreakdown(d.channel_breakdown || []);
        } catch (e) { setError('Failed to load chatter earnings'); }
        finally { setLoading(false); }
    };

    const loadChatterSelfEarnings = async () => {
        try {
            const res = await api.get('/api/chatter/earnings');
            const d = res.data;
            setEarnings({ total_earned: d.stars_total, available_balance: 0, pending_balance: 0, total_withdrawn: 0 });
            setTransactions(d.transactions || []);
            setCryptoEarnings({ admin_share_usd: d.crypto_admin_share_usd || 0 });
            setStarsPerUsd(d.stars_per_usd || 77);
            setAdminSharePct((d.admin_share_percent ?? 80) / 100);
            setDailyEarnings(d.daily_earnings || []);
            setChannelBreakdown(d.channel_breakdown || []);
            setTotalEarnedAllUsd(d.total_earned_usd || 0);
        } catch (e) { setError(e.response?.data?.error || e.message || 'Failed to load earnings'); }
        finally { setLoading(false); }
    };

    const loadEarnings = async () => {
        try {
            const res = await api.post('/api/dashboard/earnings', { token: localStorage.getItem('token') });
            setEarnings(res.data.earnings);
            setTransactions(res.data.transactions || []);
            setPayouts(res.data.payouts || []);
            setCryptoEarnings(res.data.crypto_earnings || null);
            setCryptoBreakdown(res.data.crypto_breakdown || null);
            setUsdHoldingPeriodDays(res.data.usd_holding_period_days || 14);
            setMinPayout(res.data.min_payout || 1000);
            setStarsPerUsd(res.data.stars_per_usd || 77);
            setAdminSharePct((res.data.admin_share_percent ?? 80) / 100);
            setHoldingPeriodDays(res.data.holding_period_days || 21);
            if (res.data.channel_breakdown) setChannelBreakdown(res.data.channel_breakdown);
            if (res.data.subscription_earnings) setSubscriptionEarnings(res.data.subscription_earnings);
            if (res.data.total_earned_all_usd != null) setTotalEarnedAllUsd(res.data.total_earned_all_usd);
            if (res.data.transfer_mode) setTransferMode(res.data.transfer_mode);
        } catch (e) { setError('Failed to load earnings'); }
        finally { setLoading(false); }
    };

    const loadDailyEarnings = async (days = 30) => {
        try {
            const endpoint = isChatter ? `/api/chatter/daily-earnings?days=${days}` : `/api/dashboard/daily-earnings?days=${days}`;
            const res = await api.get(endpoint);
            setDailyEarnings(res.data.daily_earnings || []);
        } catch (e) { /* silent */ }
    };

    // Reload chart when period changes
    useEffect(() => {
        if (!adminViewChatter) {
            const period = CHART_PERIODS.find(p => p.value === chartPeriod);
            loadDailyEarnings(period?.days ?? 30);
        }
    }, [chartPeriod]);

    // ─── Live Transaction Polling ────────────────────────────────────────────
    // Seed seenTxIds on first load
    useEffect(() => {
        if (transactions.length > 0 && seenTxIds.current.size === 0)
            transactions.forEach(t => { if (t.transaction_id) seenTxIds.current.add(t.transaction_id); });
    }, [transactions]);

    const pollTransactions = useCallback(async () => {
        try {
            let newTxns = [];
            let responseData = null;
            if (adminViewChatter) {
                const r = await api.get(`/api/admin/chatters/earnings?chatter_id=${adminViewChatter.id}`);
                responseData = r.data;
                newTxns = r.data.transactions || [];
            } else if (isChatter) {
                const r = await api.get('/api/chatter/earnings');
                responseData = r.data;
                newTxns = r.data.transactions || [];
            } else {
                const r = await api.post('/api/dashboard/earnings', { token: localStorage.getItem('token') });
                responseData = r.data;
                newTxns = r.data.transactions || [];
            }
            if (!newTxns.length || seenTxIds.current.size === 0) return;
            const fresh = newTxns.filter(t => t.transaction_id && !seenTxIds.current.has(t.transaction_id));
            if (!fresh.length) return;
            fresh.forEach(t => seenTxIds.current.add(t.transaction_id));
            setTransactions(prev => {
                const existing = new Set(prev.map(t => t.transaction_id));
                const added = fresh.filter(t => !existing.has(t.transaction_id)).map(t => ({ ...t, _isNew: true }));
                return added.length ? [...added, ...prev] : prev;
            });
            // Update earnings totals from the fresh response
            if (responseData) {
                if (adminViewChatter || isChatter) {
                    const d = responseData;
                    setEarnings({ total_earned: d.stars_total, available_balance: 0, pending_balance: 0, total_withdrawn: 0 });
                    setCryptoEarnings({ admin_share_usd: d.crypto_admin_share_usd || 0 });
                    if (d.stars_per_usd) setStarsPerUsd(d.stars_per_usd);
                    if (d.admin_share_percent) setAdminSharePct(d.admin_share_percent / 100);
                    if (d.daily_earnings) setDailyEarnings(d.daily_earnings);
                    if (isChatter) {
                        if (d.channel_breakdown) setChannelBreakdown(d.channel_breakdown);
                        if (d.total_earned_usd != null) setTotalEarnedAllUsd(d.total_earned_usd);
                    }
                } else {
                    const d = responseData;
                    if (d.earnings) setEarnings(d.earnings);
                    if (d.crypto_earnings) setCryptoEarnings(d.crypto_earnings);
                    if (d.crypto_breakdown) setCryptoBreakdown(d.crypto_breakdown);
                    if (d.channel_breakdown) setChannelBreakdown(d.channel_breakdown);
                    if (d.subscription_earnings) setSubscriptionEarnings(d.subscription_earnings);
                    if (d.total_earned_all_usd != null) setTotalEarnedAllUsd(d.total_earned_all_usd);
                    if (d.admin_share_percent) setAdminSharePct(d.admin_share_percent / 100);
                    if (d.daily_earnings) setDailyEarnings(d.daily_earnings);
                }
            }
            const latest = fresh[0];
            const isUsd = latest?.payment_method === 'crypto' || latest?.payment_method === 'card';
            const amt = isUsd ? `$${parseFloat(latest.amount_usd || 0).toFixed(2)}` : `$${((latest?.admin_share || 0) / starsPerUsd).toFixed(2)}`;
            toast.success(fresh.length === 1 ? `New Sale · +${amt}` : `${fresh.length} new sales`);
        } catch { /* silent */ }
    }, [isChatter, adminViewChatter, toast, starsPerUsd, adminSharePct]);

    useEffect(() => {
        const interval = setInterval(pollTransactions, 30000);
        return () => clearInterval(interval);
    }, [pollTransactions]);

    const loadWallet = async () => {
        try {
            const res = await api.post('/api/dashboard/settings', { token: localStorage.getItem('token') });
            if (res.data.settings?.usdt_wallet_address) setUsdtWallet(res.data.settings.usdt_wallet_address);
        } catch (e) { /* silent */ }
    };

    const saveUsdtWallet = async () => {
        setSavingWallet(true); setError('');
        try {
            const res = await api.post('/api/dashboard/set-usdt-wallet', { token: localStorage.getItem('token'), usdt_wallet_address: usdtWallet });
            setSuccess(res.data.message);
            setTimeout(() => setSuccess(''), 3000);
        } catch (e) { setError(e.response?.data?.error || 'Failed to save wallet'); }
        finally { setSavingWallet(false); }
    };

    // ─── Calculations ────────────────────────────────────────────────────────
    const s2u = (stars) => (stars || 0) / starsPerUsd;

    const starTotalUsd = s2u(earnings?.total_earned || 0);
    const starAvailableUsd = s2u(earnings?.available_balance || 0);
    const starPendingUsd = s2u(earnings?.pending_balance || 0);

    const usdTotalEarned = cryptoBreakdown?.total_earned_usd || 0;
    const usdAvailable = cryptoBreakdown?.available_usd || 0;
    const usdPending = cryptoBreakdown?.pending_usd || 0;
    const usdWithdrawn = cryptoBreakdown?.withdrawn_usd || 0;
    const usdPendingTransfer = cryptoBreakdown?.pending_transfer_usd || 0;

    const totalEarnedUsd = totalEarnedAllUsd != null ? totalEarnedAllUsd : (starTotalUsd + usdTotalEarned);
    const combinedAvailable = starAvailableUsd + usdAvailable;
    // For v2, pending_transfer_usd is the single source of truth for processing amounts.
    // Don't also add transaction-derived pending (usdPending) — they're the same money.
    const combinedPending = starPendingUsd + (usdPendingTransfer > 0 ? usdPendingTransfer : usdPending);
    const minPayoutUsd = s2u(minPayout);
    const minUsdPayout = 0.01;

    // 30-day earnings comparison (computed from transactions for accuracy)
    const _now = new Date();
    const _30ago = new Date(_now.getTime() - 30 * 86400000);
    const _60ago = new Date(_now.getTime() - 60 * 86400000);
    const _isUsd = (t) => t.payment_method === 'crypto' || t.payment_method === 'card';
    const _netEarn = (t) => {
        return _isUsd(t) ? parseFloat(t.amount_usd || 0) : s2u(t.admin_share || 0);
    };
    const sum30 = transactions.filter(t => new Date(t.created_at) >= _30ago).reduce((a, t) => a + _netEarn(t), 0);
    const sumPrev30 = transactions.filter(t => { const d = new Date(t.created_at); return d >= _60ago && d < _30ago; }).reduce((a, t) => a + _netEarn(t), 0);
    const growthPct = sumPrev30 > 0 ? ((sum30 - sumPrev30) / sumPrev30) * 100 : (sum30 > 0 ? 100 : 0);

    // ─── Payout Handlers ─────────────────────────────────────────────────────
    const requestUsdPayout = async () => {
        const amount = parseFloat(usdPayoutAmount);
        if (!amount || amount < minUsdPayout) { setError(`Minimum USD payout is $${minUsdPayout.toFixed(2)}`); return; }
        if (amount > usdAvailable) { setError(`Insufficient available USD balance. You have ${fmtUsd(usdAvailable)} available.`); return; }
        setRequesting(true); setError('');
        try {
            const res = await api.post('/api/dashboard/payout/request', { token: localStorage.getItem('token'), payout_type: 'usd', amount_usd: amount });
            setSuccess(res.data.message); setUsdPayoutAmount(''); loadEarnings();
        } catch (e) { setError(e.response?.data?.error || 'Failed to request payout'); }
        finally { setRequesting(false); }
    };

    // ─── Transaction Helpers ─────────────────────────────────────────────────
    const findWheel = useCallback((ch, ccid) => {
        if (ch) {
            const w = wheels.find(w => w.channel_username === ch || w.channel_username === ch.replace(/^@/, ''));
            if (w) return w;
        }
        if (ccid) {
            const absId = Math.abs(Number(ccid));
            return wheels.find(w =>
                (w.linked_chat_id && Math.abs(Number(w.linked_chat_id)) === absId) ||
                (w.channel_id && Math.abs(Number(w.channel_id)) === absId)
            );
        }
        return null;
    }, [wheels]);

    const getChannelForTx = useCallback((t) => {
        // 1. Match by wheel (channel_username or channel_chat_id)
        const w = findWheel(t.channel_username, t.channel_chat_id);
        if (w) return w.title || ('@' + w.channel_username);
        // 2. Non-numeric channel_username from backend
        if (t.channel_username && !t.channel_username.match(/^-?\d+$/))
            return '@' + t.channel_username;
        // 3. Look up from channelBreakdown by channel_chat_id (chatters: breakdown has resolved titles)
        if (t.channel_chat_id && channelBreakdown.length > 0) {
            const abs = Math.abs(Number(t.channel_chat_id));
            const cb = channelBreakdown.find(c => c.channel_chat_id && Math.abs(Number(c.channel_chat_id)) === abs);
            if (cb && cb.title) return cb.title;
        }
        // 4. Single-channel fallback: if only one wheel assigned, all txns belong to it
        if (wheels.length === 1)
            return wheels[0].title || ('@' + wheels[0].channel_username);
        return '';
    }, [findWheel, channelBreakdown, wheels]);

    // Display gross amounts — fees are deducted on Whop, not shown here
    const getNetEarning = (t) => {
        if (t.payment_method === 'crypto' || t.payment_method === 'card') return parseFloat(t.amount_usd || 0);
        return s2u(t.admin_share || 0);
    };
    const getGrossAmount = (t) => {
        if (t.payment_method === 'crypto' || t.payment_method === 'card') return parseFloat(t.amount_usd || 0);
        return s2u(t.amount_stars || 0);
    };
    const getFee = () => 0;

    // Sorted + filtered transactions
    const allTransactions = useMemo(() =>
        [...transactions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
        [transactions]);
    const txChannelOptions = useMemo(() => {
        const seen = new Set();
        allTransactions.forEach(t => { const ch = getChannelForTx(t); if (ch) seen.add(ch); });
        return [{ value: 'All', label: 'All Channels' }, ...[...seen].sort().map(ch => ({ value: ch, label: ch }))];
    }, [allTransactions, getChannelForTx]);
    const filteredTransactions = useMemo(() => allTransactions.filter(t => {
        if (txFilterType !== 'All' && t.purchase_type !== TYPE_MAP[txFilterType]) return false;
        if (txFilterChannel !== 'All' && getChannelForTx(t) !== txFilterChannel) return false;
        if (txSearch) {
            const q = txSearch.toLowerCase();
            const ch = getChannelForTx(t).toLowerCase();
            const type = purchaseLabel(t.purchase_type).toLowerCase();
            const txId = (t.transaction_id || '').toLowerCase();
            if (!ch.includes(q) && !type.includes(q) && !txId.includes(q)) return false;
        }
        return true;
    }), [allTransactions, txFilterType, txFilterChannel, txSearch, getChannelForTx]);
    const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE));
    const pagedTransactions = filteredTransactions.slice((txPage - 1) * ITEMS_PER_PAGE, txPage * ITEMS_PER_PAGE);
    const { txByChannel, userChannels } = useMemo(() => {
        const byChannel = {};
        transactions.forEach(t => {
            const ch = t.channel_username || '';
            const wheel = findWheel(ch, t.channel_chat_id);
            const key = wheel ? wheel.channel_username : (ch || 'Unknown');
            if (!byChannel[key]) byChannel[key] = { usd: 0, count: 0 };
            if (t.purchase_type === 'referral') byChannel[key].usd += parseFloat(t.amount_usd || 0);
            else if (t.payment_method === 'crypto' || t.payment_method === 'card') byChannel[key].usd += parseFloat(t.amount_usd || 0);
            else byChannel[key].usd += s2u(t.admin_share || 0);
            byChannel[key].count += 1;
        });
        const channels = Object.keys(byChannel).sort((a, b) => byChannel[b].usd - byChannel[a].usd);
        return { txByChannel: byChannel, userChannels: channels };
    }, [transactions, wheels, adminSharePct]);

    // ─── Chart Data (zero-filled for selected period) ────────────────────────
    const isHourly = chartPeriod === 'today' || chartPeriod === 'yesterday';
    const chartData = useMemo(() => {
        const result = [];
        const mkVal = (d) => s2u(d.earned || 0) + (d.crypto_usd || 0);
        if (isHourly) {
            const lookup = {};
            dailyEarnings.forEach(d => { if (d.hour != null) lookup[d.hour] = d; });
            for (let h = 0; h < 24; h++) {
                const d = lookup[h] || { hour: h, earned: 0, crypto_usd: 0, transactions: 0 };
                result.push({ ...d, hour: h, label: `${h}:00`, earned_usd: mkVal(d) });
            }
        } else {
            const lookup = {};
            dailyEarnings.forEach(d => { lookup[d.date] = d; });
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const numDays = (CHART_PERIODS.find(p => p.value === chartPeriod)?.days) || 30;
            for (let i = numDays - 1; i >= 0; i--) {
                const dt = new Date(today); dt.setDate(dt.getDate() - i);
                const dateStr = localDateStr(dt);
                const d = lookup[dateStr] || { date: dateStr, earned: 0, crypto_usd: 0, transactions: 0 };
                result.push({ ...d, date: dateStr, earned_usd: mkVal(d) });
            }
        }
        return result;
    }, [dailyEarnings, chartPeriod, adminSharePct]);
    const periodLabel = CHART_PERIODS.find(p => p.value === chartPeriod)?.label || 'Last 30 Days';

    // ─── Render helpers ─────────────────────────────────────────────────────
    const renderLoading = () => (
        <div style={{ textAlign: 'center', padding: '80px 40px' }}>
            <img src="/logo-light.webp" alt="Loading" style={{ height: '28px', opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '16px' }}>Loading your earnings...</div>
        </div>
    );

    const renderHeader = () => (
        !fullPage && (
        <div style={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={styles.headerIcon}><Icons.Wallet /></div>
                <div>
                    <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                        {adminViewChatter ? `${adminViewChatter.display_name || adminViewChatter.username} Earnings` : 'Earnings'}
                    </h2>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{isChatter ? 'Your chatter earnings' : 'Creator earnings dashboard'}</div>
                </div>
            </div>
            <button onClick={onClose} style={{ ...btnSmall, width: '36px', height: '36px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px' }}><Icons.X /></button>
        </div>
        )
    );

    // ─── Render functions (defined before conditional returns) ──────────
    const renderContent = () => (
        <>
                    {/* Messages */}
                    {error && <div style={styles.errorMsg}>{error} <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: 'var(--error)', cursor: 'pointer', marginLeft: '8px', fontSize: '16px' }}>&times;</button></div>}
                    {success && <div style={styles.successMsg}>{success}</div>}

                    {/* Payment note for chatters */}
                    {(isChatter || adminViewChatter) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '10px', background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', marginBottom: '16px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(139,92,246,0.8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                            <span>Payments are handled by your creator outside the platform. This dashboard tracks your earnings for reference only.</span>
                        </div>
                    )}


                    {/* ══════════════════════════════════════════════════════════
                        TOP SUMMARY CARD
                    ══════════════════════════════════════════════════════════ */}
                    <div style={styles.summaryCard}>
                        <div style={styles.summaryGlow} />
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div style={styles.summaryGrid}>
                                {/* Left: balances */}
                                <div style={{ flex: 1, minWidth: '280px' }}>
                                    {isChatter ? (
                                        /* Chatter view: show Total Revenue + Last 30 days */
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                                            <div>
                                                <div style={styles.summaryLabel}>Total Revenue</div>
                                                <div style={styles.summaryBigNum}>{fmtUsd(totalEarnedUsd)}</div>
                                            </div>
                                            <div style={styles.summaryDivider} />
                                            <div>
                                                <div style={styles.summaryLabel}>Last 30 Days</div>
                                                <div style={{ ...styles.summaryMedNum }}>
                                                    {fmtUsd(sum30)}
                                                    {(sum30 > 0 || sumPrev30 > 0) && (
                                                        <span style={{ ...styles.growthBadge, background: growthPct >= 0 ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: growthPct >= 0 ? '#10b981' : '#ef4444', marginLeft: '8px', fontSize: '11px' }}>
                                                            {growthPct >= 0 ? '▲' : '▼'} {Math.abs(growthPct).toFixed(1)}%
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        /* Creator view: Total Revenue + Last 30 days with % change */
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                                            <div>
                                                <div style={styles.summaryLabel}>Total Revenue</div>
                                                <div style={styles.summaryBigNum}>{fmtUsd(totalEarnedUsd)}</div>
                                            </div>
                                            <div style={styles.summaryDivider} />
                                            <div>
                                                <div style={styles.summaryLabel}>Last 30 Days</div>
                                                <div style={{ ...styles.summaryMedNum }}>
                                                    {fmtUsd(sum30)}
                                                    {(sum30 > 0 || sumPrev30 > 0) && (
                                                        <span style={{ ...styles.growthBadge, background: growthPct >= 0 ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: growthPct >= 0 ? '#10b981' : '#ef4444', marginLeft: '8px', fontSize: '11px' }}>
                                                            {growthPct >= 0 ? '▲' : '▼'} {Math.abs(growthPct).toFixed(1)}%
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Right: withdraw button */}
                                {!isChatter && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                                    <button
                                        onClick={() => setShowWithdrawSection(!showWithdrawSection)}
                                        style={styles.withdrawBtn}
                                    >
                                        <Icons.Send /> Manage Payouts
                                    </button>
                                </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ══════════════════════════════════════════════════════════
                        BSC PAYOUT SECTION (expandable)
                    ══════════════════════════════════════════════════════════ */}
                    {!isChatter && showWithdrawSection && (
                    <div style={styles.withdrawPanel}>
                        <PayoutSection />
                    </div>
                    )}

                    {/* ══════════════════════════════════════════════════════════
                        EARNINGS CHART
                    ══════════════════════════════════════════════════════════ */}
                    {chartData.length > 0 && (() => {
                        const maxEarned = Math.max(...chartData.map(d => d.earned_usd), 0.01);
                        const chartWidth = 700;
                        const chartHeight = 200;
                        const padding = { top: 24, right: 16, bottom: 48, left: 52 };
                        const innerWidth = chartWidth - padding.left - padding.right;
                        const innerHeight = chartHeight - padding.top - padding.bottom;

                        const points = chartData.map((d, i) => ({
                            x: padding.left + (i / (chartData.length - 1 || 1)) * innerWidth,
                            y: Math.min(padding.top + innerHeight - 1, padding.top + innerHeight - (d.earned_usd / maxEarned) * innerHeight),
                            ...d
                        }));

                        const yMin = padding.top;
                        const yMax = padding.top + innerHeight;
                        const clampY = (v) => Math.max(yMin, Math.min(yMax, v));
                        const createSmoothPath = (pts) => {
                            if (pts.length < 2) return '';
                            let path = `M ${pts[0].x} ${pts[0].y}`;
                            for (let i = 0; i < pts.length - 1; i++) {
                                const p0 = pts[i - 1] || pts[i];
                                const p1 = pts[i];
                                const p2 = pts[i + 1];
                                const p3 = pts[i + 2] || p2;
                                const cp1x = p1.x + (p2.x - p0.x) / 6;
                                const cp1y = clampY(p1.y + (p2.y - p0.y) / 6);
                                const cp2x = p2.x - (p3.x - p1.x) / 6;
                                const cp2y = clampY(p2.y - (p3.y - p1.y) / 6);
                                path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
                            }
                            return path;
                        };

                        const linePath = createSmoothPath(points);
                        const areaPath = linePath + ` L ${points[points.length - 1]?.x || 0} ${yMax} L ${padding.left} ${yMax} Z`;
                        const yLabels = [0, maxEarned * 0.25, maxEarned * 0.5, maxEarned * 0.75, maxEarned];

                        return (
                        <div style={styles.section}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                                <div style={styles.sectionHeader}>
                                    <Icons.TrendingUp /> <span>Earnings</span>
                                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '4px' }}>{periodLabel}</span>
                                </div>
                                <CustomSelect value={chartPeriod} onChange={v => setChartPeriod(v)}
                                    options={CHART_PERIODS.map(p => ({ value: p.value, label: p.label }))}
                                    style={{ minWidth: '150px' }} />
                            </div>

                            <div ref={chartContainerRef} style={{ position: 'relative', width: '100%', overflow: 'hidden' }} onMouseLeave={() => setChartTooltip(null)}>
                                <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} style={{ width: '100%', height: 'auto', display: 'block' }}
                                    onMouseMove={(e) => {
                                        const container = chartContainerRef.current;
                                        if (!container || points.length === 0) return;
                                        const svg = e.currentTarget;
                                        const svgRect = svg.getBoundingClientRect();
                                        const containerRect = container.getBoundingClientRect();
                                        const scaleX = svgRect.width / chartWidth;
                                        const scaleY = svgRect.height / chartHeight;
                                        const mouseXInSvg = (e.clientX - svgRect.left) / scaleX;
                                        let nearest = 0;
                                        let minDist = Math.abs(points[0].x - mouseXInSvg);
                                        for (let idx = 1; idx < points.length; idx++) {
                                            const dist = Math.abs(points[idx].x - mouseXInSvg);
                                            if (dist < minDist) { minDist = dist; nearest = idx; }
                                        }
                                        const pt = points[nearest];
                                        const date = new Date(pt.date);
                                        const daysPassed = (new Date() - date) / (1000 * 60 * 60 * 24);
                                        const isPending = daysPassed <= holdingPeriodDays;
                                        const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                        const pointXInContainer = (svgRect.left - containerRect.left) + pt.x * scaleX;
                                        const pointYInContainer = (svgRect.top - containerRect.top) + pt.y * scaleY;
                                        setChartTooltip({ x: pointXInContainer, y: pointYInContainer, svgX: pt.x, containerWidth: containerRect.width, date: formattedDate, earned_usd: pt.earned_usd, transactions: pt.transactions, isPending });
                                    }}>
                                    <defs>
                                        <linearGradient id="areaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                                            <stop offset="0%" stopColor="#00AEEF" stopOpacity="0.35" />
                                            <stop offset="100%" stopColor="#00AEEF" stopOpacity="0.02" />
                                        </linearGradient>
                                        <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                            <stop offset="0%" stopColor="#00AEEF" />
                                            <stop offset="100%" stopColor="#10b981" />
                                        </linearGradient>
                                    </defs>

                                    {/* Grid lines */}
                                    {yLabels.map((val, i) => {
                                        const y = padding.top + innerHeight - (val / maxEarned) * innerHeight;
                                        return (
                                            <g key={i}>
                                                <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="var(--border)" strokeDasharray="3 3" opacity="0.5" />
                                                <text x={padding.left - 8} y={y + 4} fill="var(--text-muted)" fontSize="9" textAnchor="end" fontFamily={fontStack}>${val.toFixed(val >= 1 ? 0 : 2)}</text>
                                            </g>
                                        );
                                    })}

                                    <path d={areaPath} fill="url(#areaGrad)" />
                                    <path d={linePath} fill="none" stroke="url(#lineGrad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

                                    {/* Hover line */}
                                    {chartTooltip && chartTooltip.svgX != null && (
                                        <line x1={chartTooltip.svgX} y1={padding.top} x2={chartTooltip.svgX} y2={yMax} stroke="var(--accent)" strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
                                    )}

                                    {/* Hovered dot only */}
                                    {chartTooltip && points.map((pt, i) => {
                                        const isHovered = Math.abs((chartTooltip.svgX || 0) - pt.x) < 2;
                                        return isHovered ? (
                                            <circle key={i} cx={pt.x} cy={pt.y} r={5}
                                                fill="#00AEEF"
                                                stroke="var(--modal-bg)" strokeWidth={3} />
                                        ) : null;
                                    })}

                                    {/* X labels */}
                                    {(() => {
                                        if (isHourly) {
                                            return chartData.map((d, i) => {
                                                if (i % 3 !== 0 && i !== chartData.length - 1) return null;
                                                const x = padding.left + (i / (chartData.length - 1 || 1)) * innerWidth;
                                                return <text key={i} x={x} y={yMax + 18} fill="var(--text-muted)" fontSize="9" textAnchor="middle" fontFamily={fontStack}>{d.label}</text>;
                                            });
                                        }
                                        const monthOnly = chartData.length >= 90;
                                        if (monthOnly) {
                                            const showYear = chartData.length > 180;
                                            const months = {};
                                            chartData.forEach((d, i) => {
                                                const [y, m] = d.date.split('-');
                                                const key = `${y}-${m}`;
                                                if (!months[key]) months[key] = { y, m: parseInt(m, 10), start: i, end: i };
                                                else months[key].end = i;
                                            });
                                            const names = ['', 'Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                                            return Object.entries(months).map(([key, { y, m, start, end }]) => {
                                                const mid = (start + end) / 2;
                                                const label = showYear ? `${names[m]} '${y.slice(2)}` : names[m];
                                                const x = padding.left + (mid / (chartData.length - 1 || 1)) * innerWidth;
                                                return <text key={key} x={x} y={yMax + 18} fill="var(--text-muted)" fontSize="9" textAnchor="middle" fontFamily={fontStack}>{label}</text>;
                                            });
                                        }
                                        const step = chartData.length <= 10 ? 1 : chartData.length <= 30 ? 3 : 7;
                                        return chartData.map((d, i) => {
                                            if (i % step !== 0) return null;
                                            const label = new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                            const x = padding.left + (i / (chartData.length - 1 || 1)) * innerWidth;
                                            return <text key={i} x={x} y={yMax + 18} fill="var(--text-muted)" fontSize="9" textAnchor="middle" fontFamily={fontStack}>{label}</text>;
                                        });
                                    })()}
                                </svg>

                                {/* Tooltip */}
                                {chartTooltip && (() => {
                                    const tw = 130; const th = 60; const gap = 12;
                                    const cw = chartTooltip.containerWidth || 500;
                                    let top = chartTooltip.y - th - gap;
                                    if (top < 0) top = chartTooltip.y + gap + 10;
                                    let left = chartTooltip.x + gap + tw < cw - 4 ? chartTooltip.x + gap : chartTooltip.x - gap - tw;
                                    if (left < 4) left = 4;
                                    return (
                                        <div style={{ position: 'absolute', left, top, width: tw, boxSizing: 'border-box', background: 'var(--modal-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 12px', pointerEvents: 'none', zIndex: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px' }}>{chartTooltip.label || chartTooltip.date}</div>
                                            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent)' }}>${chartTooltip.earned_usd?.toFixed(2)}</div>
                                            <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '3px' }}>{chartTooltip.transactions} sale{chartTooltip.transactions !== 1 ? 's' : ''}</div>
                                        </div>
                                    );
                                })()}
                            </div>

                        </div>
                        );
                    })()}

                    {/* ══════════════════════════════════════════════════════════
                        EARNINGS BY CHANNEL
                    ══════════════════════════════════════════════════════════ */}
                    {(adminViewChatter || isChatter) && channelBreakdown.length > 0 ? (
                        <div style={styles.section}>
                            <div style={styles.sectionHeader}><Icons.ChartBar /> <span>Earnings by Channel</span></div>
                            <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                                {channelBreakdown.map((ch, i) => {
                                    const w = findWheel('', ch.channel_chat_id);
                                    const displayName = (w ? (w.title || ('@' + w.channel_username)) : null) || ch.title || 'Channel';
                                    return (
                                        <div key={ch.channel_chat_id || i} style={styles.channelRow(i < channelBreakdown.length - 1)}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={styles.channelAvatar}>{displayName[0].toUpperCase()}</div>
                                                <div>
                                                    <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '13px' }}>{displayName}</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{ch.count} sale{ch.count !== 1 ? 's' : ''}</div>
                                                </div>
                                            </div>
                                            <div style={{ fontWeight: 700, color: '#10b981', fontSize: '14px' }}>${ch.earned_usd.toFixed(2)}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : userChannels.length > 0 && !adminViewChatter ? (
                        <div style={styles.section}>
                            <div style={styles.sectionHeader}><Icons.ChartBar /> <span>Earnings by Channel</span></div>
                            <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                                {userChannels.map((ch, i) => {
                                    const chData = txByChannel[ch];
                                    const wheel = wheels.find(w => w.channel_username === ch);
                                    const title = wheel?.title || (ch === 'Unknown' ? 'Other' : ch);
                                    const subtitle = ch === 'Unknown' ? `${chData.count} sales` : `@${ch} · ${chData.count} sales`;
                                    return (
                                        <div key={ch} style={styles.channelRow(i < userChannels.length - 1)}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={styles.channelAvatar}>{title[0].toUpperCase()}</div>
                                                <div>
                                                    <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '13px' }}>{title}</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{subtitle}</div>
                                                </div>
                                            </div>
                                            <div style={{ fontWeight: 700, color: '#10b981', fontSize: '14px' }}>${chData.usd.toFixed(2)}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    {/* ══════════════════════════════════════════════════════════
                        SUBSCRIPTIONS
                    ══════════════════════════════════════════════════════════ */}
                    {!isChatter && subscriptionEarnings && (subscriptionEarnings.active_subscribers > 0 || subscriptionEarnings.total_earned_usd > 0) && (
                    <div style={styles.section}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div style={styles.sectionHeader}>
                                <Icons.Users /> <span>Subscriptions</span>
                            </div>
                            {subscriptionEarnings.total_earned_usd === 0 && subscriptionEarnings.active_subscribers > 0 && (
                                <button onClick={async () => {
                                    try {
                                        const res = await api.post('/api/subscriptions/backfill-transactions', { token: localStorage.getItem('token') });
                                        if (res.data.created > 0) {
                                            setSuccess(`Backfilled ${res.data.created} subscription transaction${res.data.created !== 1 ? 's' : ''}`);
                                            loadEarnings();
                                            const period = CHART_PERIODS.find(p => p.value === chartPeriod);
                                            loadDailyEarnings(period?.days ?? 30);
                                        } else {
                                            setSuccess('No missing transactions found');
                                        }
                                        setTimeout(() => setSuccess(''), 3000);
                                    } catch (e) { setError('Failed to backfill'); }
                                }} style={{ ...btnSecondary, fontSize: '11px', padding: '5px 12px' }}>
                                    Sync Past Payments
                                </button>
                            )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                            <div style={styles.miniStat}>
                                <div style={styles.miniStatLabel}>Active</div>
                                <div style={styles.miniStatValue}>{subscriptionEarnings.active_subscribers}</div>
                            </div>
                            <div style={styles.miniStat}>
                                <div style={styles.miniStatLabel}>Total Revenue</div>
                                <div style={{ ...styles.miniStatValue, color: '#10b981' }}>${(subscriptionEarnings.total_earned_usd || 0).toFixed(2)}</div>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* ══════════════════════════════════════════════════════════
                        TRANSACTION HISTORY
                    ══════════════════════════════════════════════════════════ */}
                    <div style={styles.section}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={styles.sectionHeader}><Icons.Clock /> <span>Transaction History</span></div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}</div>
                        </div>

                        {/* Search + Filter */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: '180px', position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}><Icons.Search /></span>
                                <input type="text" value={txSearch} onChange={e => { setTxSearch(e.target.value); setTxPage(1); }}
                                    placeholder="Search transactions..."
                                    style={{ ...styles.amountInput, paddingLeft: '34px', width: '100%' }} />
                            </div>
                            <div style={{ position: 'relative' }}>
                                <CustomSelect value={txFilterType} onChange={v => { setTxFilterType(v); setTxPage(1); }}
                                    options={[
                                        { value: 'All', label: 'All Types' },
                                        ...(!isChatter && !adminViewChatter ? [{ value: 'Subscriptions', label: 'Subscriptions' }] : []),
                                        { value: 'Tips', label: 'Tips' },
                                        { value: 'Messages', label: 'Messages' },
                                        ...(!isChatter && !adminViewChatter ? [{ value: 'Spins', label: 'Spins' }] : []),
                                    ]}
                                    style={{ minWidth: '140px' }} />
                            </div>
                            {txChannelOptions.length > 1 && (
                                <div style={{ position: 'relative' }}>
                                    <CustomSelect value={txFilterChannel} onChange={v => { setTxFilterChannel(v); setTxPage(1); }}
                                        options={txChannelOptions}
                                        style={{ minWidth: '140px' }} />
                                </div>
                            )}
                        </div>

                        {/* Table */}
                        {pagedTransactions.length === 0 ? (
                            <div style={{ background: 'var(--input-bg)', borderRadius: '10px', padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', border: '1px solid var(--border)' }}>
                                {allTransactions.length === 0 ? 'No sales yet. Share your channel link to start earning!' : 'No transactions match your filters.'}
                            </div>
                        ) : (
                            <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                                {/* Header row */}
                                <div style={styles.tableHeader}>
                                    <div style={{ flex: 2, minWidth: '120px' }}>Customer</div>
                                    <div style={{ flex: 2, minWidth: '100px' }}>Channel</div>
                                    <div style={{ flex: 2, minWidth: '100px' }}>Type</div>
                                    <div style={{ flex: 1, minWidth: '80px', textAlign: 'right' }}>Amount</div>
                                    <div style={{ width: '28px' }} />
                                </div>
                                {/* Rows */}
                                {pagedTransactions.map((t, i) => {
                                    const gross = getGrossAmount(t);
                                    const fee = getFee(t);
                                    const net = getNetEarning(t);
                                    const pColor = purchaseColor(t.purchase_type);
                                    return (
                                        <div key={t.transaction_id || i}
                                            onClick={() => setDetailTx(t)}
                                            style={{ ...styles.tableRow(i < pagedTransactions.length - 1), ...(t._isNew ? { animation: 'txRowIn 1.2s ease' } : {}) }}>
                                            <div style={{ flex: 2, minWidth: '120px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {t.purchase_type === 'referral' ? (<><span>Privly</span><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#1d9bf0"/><path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg></>) : (t.buyer_username ? `@${t.buyer_username}` : t.buyer_name || `User ${t.user_id || ''}`)}
                                                </div>
                                                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '1px' }}>{fmtDateTime(t.created_at)}</div>
                                            </div>
                                            <div style={{ flex: 2, minWidth: '100px' }}>
                                                <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)' }}>{t.purchase_type === 'referral' ? '-' : (getChannelForTx(t) || '')}</div>
                                            </div>
                                            <div style={{ flex: 2, minWidth: '100px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ ...styles.typeBadge, background: pColor + '18', color: pColor }}>{purchaseIcon(t.purchase_type)}</span>
                                                <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)' }}>{purchaseLabel(t.purchase_type)}</span>
                                            </div>
                                            <div style={{ flex: 1, minWidth: '80px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: gross < 0 ? '#ef4444' : t.purchase_type === 'trial' ? '#9ca3af' : '#10b981' }}>{t.purchase_type === 'trial' ? '$0.00' : gross < 0 ? `-${fmtUsd(Math.abs(gross))}` : `+${fmtUsd(gross)}`}</div>
                                            <div style={{ width: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}><Icons.ChevronRight /></div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <style>{`@keyframes txRowIn { 0% { background: rgba(16,185,129,0.15); } 100% { background: transparent; } }`}</style>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div style={styles.pagination}>
                                <button onClick={() => setTxPage(p => Math.max(1, p - 1))} disabled={txPage === 1} style={styles.pageBtn}>
                                    <Icons.ChevronLeft />
                                </button>
                                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                    Page {txPage} of {totalPages}
                                </span>
                                <button onClick={() => setTxPage(p => Math.min(totalPages, p + 1))} disabled={txPage === totalPages} style={styles.pageBtn}>
                                    <Icons.ChevronRight />
                                </button>
                            </div>
                        )}
                    </div>

        </>
    );

    const renderDetailPanel = () => {
        if (!detailTx) return null;
        const isChargeback = detailTx.purchase_type === 'chargeback' || detailTx.purchase_type === 'refund';
        const detailNet = getNetEarning(detailTx);
        const detailGross = getGrossAmount(detailTx);
        const detailFee = getFee(detailTx);
        const isTrial = detailTx.purchase_type === 'trial';

        const isReferral = detailTx.purchase_type === 'referral';
        const refAmount = parseFloat(detailTx.amount_usd || 0);

        const detailRows = isReferral ? [
            ['Customer', 'Privly'],
            ['Type', 'Referral Payout'],
            ['Channel', '-'],
            ['Payment Method', 'Platform Transfer'],
            ['Amount', fmtUsd(refAmount)],
            ['Platform Fee', '$0.00'],
            ['Net Received', fmtUsd(refAmount)],
            ['Date & Time', fmtFull(detailTx.created_at)],
        ] : isTrial ? [
            ['Customer', detailTx.buyer_username ? `@${detailTx.buyer_username}` : detailTx.buyer_name || `User ${detailTx.user_id || ''}`],
            ['Type', 'Trial Subscription'],
            ['Channel', getChannelForTx(detailTx) || ''],
            ['Amount', '$0.00 — Free Trial'],
            ['Date & Time', fmtFull(detailTx.created_at)],
        ] : isChargeback ? [
            ['Customer', detailTx.buyer_username ? `@${detailTx.buyer_username}` : detailTx.buyer_name || `User ${detailTx.user_id || ''}`],
            ['Type', purchaseLabel(detailTx.purchase_type)],
            ['Channel', getChannelForTx(detailTx) || ''],
            ['Amount Reversed', `-${fmtUsd(Math.abs(detailGross))}`],
            ['Deducted from Earnings', `-${fmtUsd(Math.abs(detailNet))}`],
            ['Date & Time', fmtFull(detailTx.created_at)],
        ] : [
            ['Customer', detailTx.buyer_username ? `@${detailTx.buyer_username}` : detailTx.buyer_name || `User ${detailTx.user_id || ''}`],
            ['Type', purchaseLabel(detailTx.purchase_type)],
            ['Channel', getChannelForTx(detailTx) || ''],
            ['Payment Method', detailTx.payment_method === 'card' ? 'Card' : detailTx.payment_method === 'crypto' ? 'USD / Crypto' : 'Telegram Stars'],
            ['Amount', fmtUsd(detailGross)],
            ['Date & Time', fmtFull(detailTx.created_at)],
        ];

        return (
        <>
            <div onClick={() => setDetailTx(null)} style={styles.detailOverlay} />
            <div style={styles.detailPanel}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text)' }}>Transaction Details</h3>
                    <button onClick={() => setDetailTx(null)} style={{ ...btnSmall, width: '32px', height: '32px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}><Icons.X /></button>
                </div>

                {/* Status badge */}
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    {isTrial ? (
                        <div style={{ display: 'inline-flex', padding: '6px 16px', borderRadius: '20px', background: 'rgba(156,163,175,0.1)', color: '#9ca3af', fontSize: '12px', fontWeight: 600, alignItems: 'center', gap: '6px' }}>
                            <Icons.Users /> Free Trial
                        </div>
                    ) : isChargeback ? (
                        <div style={{ display: 'inline-flex', padding: '6px 16px', borderRadius: '20px', background: '#fef2f2', color: '#ef4444', fontSize: '12px', fontWeight: 600, alignItems: 'center', gap: '6px' }}>
                            <Icons.AlertTriangle /> {detailTx.purchase_type === 'chargeback' ? 'Chargeback' : 'Refund'}
                        </div>
                    ) : (
                        <div style={{ display: 'inline-flex', padding: '6px 16px', borderRadius: '20px', background: 'var(--success-bg)', color: 'var(--success)', fontSize: '12px', fontWeight: 600, alignItems: 'center', gap: '6px' }}>
                            <Icons.Check /> Completed
                        </div>
                    )}
                </div>

                {/* Amount display */}
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    {isTrial ? (
                        <>
                            <div style={{ fontSize: '32px', fontWeight: 800, color: '#9ca3af' }}>$0.00</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Free Trial — No Payout</div>
                        </>
                    ) : isChargeback ? (
                        <>
                            <div style={{ fontSize: '32px', fontWeight: 800, color: '#ef4444' }}>-{fmtUsd(Math.abs(detailNet))}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Amount Deducted</div>
                        </>
                    ) : (
                        <>
                            <div style={{ fontSize: '32px', fontWeight: 800, color: '#10b981' }}>+{fmtUsd(detailNet)}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Net Earnings</div>
                        </>
                    )}
                </div>

                {/* Ban notice for chargebacks */}
                {isChargeback && detailTx.purchase_type === 'chargeback' && (
                    <div style={{ display: 'flex', gap: '8px', padding: '10px 12px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', marginBottom: '16px' }}>
                        <Icons.AlertTriangle style={{ flexShrink: 0, color: '#ef4444', marginTop: '1px' }} />
                        <div style={{ fontSize: '12px', color: '#991b1b', lineHeight: '1.4' }}>
                            This user has been <b>banned platform-wide</b> and removed from all VIP channels.
                        </div>
                    </div>
                )}

                {/* Detail rows */}
                <div style={styles.detailGrid}>
                    {detailRows.map(([label, value], i) => (
                        <div key={i} style={styles.detailRow(i === detailRows.length - 1)}>
                            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{label}</span>
                            <span style={{ color: isChargeback && (label.includes('Reversed') || label.includes('Deducted')) ? '#ef4444' : 'var(--text)', fontSize: '12px', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
                        </div>
                    ))}
                </div>
            </div>
        </>
        );
    };

    // ─── Full-page render (inline in dashboard content area) ─────────────
    if (fullPage) {
        if (loading) return renderLoading();
        return (
            <>
                <div style={{ padding: '24px' }}>
                    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                        {renderContent()}
                    </div>
                </div>
                {renderDetailPanel()}
            </>
        );
    }

    // ─── Modal render (for admin viewing chatter earnings) ───────────────
    if (loading) {
        return (
            <div style={styles.backdrop}>
                <div style={{ ...styles.modal, maxWidth: '900px' }}>
                    {renderLoading()}
                </div>
            </div>
        );
    }

    return (
        <div style={styles.backdrop}>
            <div style={{ ...styles.modal, maxWidth: '900px' }} ref={scrollRef}>
                {renderHeader()}
                <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(100vh - 140px)' }}>
                    {renderContent()}
                </div>
            </div>
            {renderDetailPanel()}
        </div>
    );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = {
    backdrop: {
        position: 'fixed', inset: 0, background: 'var(--modal-backdrop)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        zIndex: 1000, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        overflowY: 'auto', padding: '20px',
    },
    modal: {
        background: 'var(--modal-bg)', backdropFilter: 'var(--modal-blur)', WebkitBackdropFilter: 'var(--modal-blur)',
        border: '1px solid var(--glass-border)', borderRadius: '20px',
        boxShadow: 'var(--card-shadow)', width: '100%', margin: '20px 0',
        padding: 0, overflow: 'hidden',
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '18px 24px', borderBottom: '1px solid var(--border)',
    },
    headerIcon: {
        width: '36px', height: '36px', borderRadius: '10px',
        background: 'linear-gradient(135deg, rgba(0,174,239,0.15), rgba(16,185,129,0.15))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--accent)',
    },

    // Summary card
    summaryCard: {
        position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(135deg, rgba(0,174,239,0.08), rgba(16,185,129,0.05), rgba(139,92,246,0.05))',
        border: '1px solid rgba(0,174,239,0.15)',
        borderRadius: '16px', padding: '24px', marginBottom: '20px',
    },
    summaryGlow: {
        position: 'absolute', top: '-50%', right: '-20%',
        width: '300px', height: '300px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,174,239,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    summaryGrid: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: '20px', flexWrap: 'wrap',
    },
    summaryLabel: {
        fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: '4px',
    },
    summaryBigNum: {
        fontSize: '34px', fontWeight: 800, color: 'var(--text)',
        letterSpacing: '-0.5px', lineHeight: 1.1,
    },
    summaryMedNum: {
        fontSize: '22px', fontWeight: 700, lineHeight: 1.1,
    },
    summaryDivider: {
        width: '1px', height: '40px', background: 'var(--border)',
        alignSelf: 'center', flexShrink: 0,
    },
    summaryStatsRow: {
        display: 'flex', alignItems: 'center', gap: '16px',
        marginTop: '16px', flexWrap: 'wrap',
    },
    summaryStat: {
        display: 'flex', flexDirection: 'column', gap: '2px',
    },
    growthBadge: {
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        padding: '4px 10px', borderRadius: '20px',
        fontSize: '12px', fontWeight: 600,
    },
    withdrawBtn: {
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '12px 24px', borderRadius: '12px',
        background: 'linear-gradient(135deg, #00AEEF, #0096D6)',
        color: '#fff', border: 'none', fontSize: '13px', fontWeight: 600,
        cursor: 'pointer', fontFamily: fontStack,
        boxShadow: '0 4px 16px rgba(0,174,239,0.3)',
        transition: 'all 0.2s ease',
    },

    // Withdraw panel
    withdrawPanel: {
        background: 'var(--surface-hover)', border: '1px solid var(--border)',
        borderRadius: '14px', padding: '20px', marginBottom: '20px',
        overflow: 'visible', position: 'relative', zIndex: 10,
        minHeight: '400px',
    },
    pillToggle: {
        display: 'flex', background: 'var(--input-bg)', border: '1px solid var(--border)',
        borderRadius: '10px', padding: '3px', marginBottom: '16px',
    },
    pillTab: {
        flex: 1, padding: '8px 16px', border: 'none', borderRadius: '8px',
        cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: fontStack,
        transition: 'all 0.2s', background: 'transparent', color: 'var(--text-muted)',
    },
    pillTabActive: {
        background: 'var(--accent)', color: '#fff',
    },
    balanceRow: {
        display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap',
    },
    balanceItem: {
        flex: 1, minWidth: '100px', background: 'var(--input-bg)',
        borderRadius: '8px', padding: '10px 12px',
        display: 'flex', flexDirection: 'column', gap: '4px',
    },
    balanceLabel: { fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase' },
    balanceValue: { fontSize: '15px', fontWeight: 700, color: 'var(--text)' },

    // Inputs
    inputLabel: { fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' },
    inputPrefix: {
        position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
        color: 'var(--text-muted)', fontSize: '14px', pointerEvents: 'none',
    },
    amountInput: {
        width: '100%', padding: '10px 12px 10px 24px', boxSizing: 'border-box',
        background: 'var(--input-bg)', border: '1px solid var(--border)',
        borderRadius: '8px', color: 'var(--text)', fontSize: '13px', fontFamily: fontStack,
        outline: 'none',
    },
    requestBtn: {
        padding: '10px 20px', borderRadius: '10px',
        background: 'var(--accent)', color: '#fff', border: 'none',
        fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: fontStack,
        whiteSpace: 'nowrap', flexShrink: 0,
    },

    // Sections
    section: {
        background: 'var(--surface-hover)', border: '1px solid var(--border)',
        borderRadius: '14px', padding: '20px', marginBottom: '20px',
    },
    sectionHeader: {
        display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: '0 0 14px 0',
        lineHeight: 1,
    },

    // Mini stat
    miniStat: {
        background: 'var(--input-bg)', borderRadius: '8px', padding: '10px 12px',
    },
    miniStatLabel: { fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase' },
    miniStatValue: { fontSize: '18px', fontWeight: 700, color: 'var(--text)', marginTop: '2px' },

    // Filter tabs
    filterRow: {
        display: 'flex', gap: '4px', flexWrap: 'wrap',
    },
    filterTab: {
        padding: '5px 12px', borderRadius: '8px', border: 'none',
        background: 'transparent', color: 'var(--text-muted)',
        fontSize: '11px', fontWeight: 500, cursor: 'pointer', fontFamily: fontStack,
        transition: 'all 0.15s',
    },
    filterTabActive: {
        background: 'rgba(0,174,239,0.12)', color: 'var(--accent)',
    },

    // Table
    tableHeader: {
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '10px 16px', background: 'var(--input-bg)',
        borderBottom: '1px solid var(--border)',
        fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.5px',
    },
    tableRow: (hasBorder) => ({
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '12px 16px', background: 'var(--input-bg)',
        borderBottom: hasBorder ? '1px solid var(--border)' : 'none',
        cursor: 'pointer', transition: 'background 0.15s',
    }),
    typeBadge: {
        width: '28px', height: '28px', borderRadius: '8px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
    },

    // Channel rows
    channelRow: (hasBorder) => ({
        padding: '12px 16px', background: 'var(--input-bg)',
        borderBottom: hasBorder ? '1px solid var(--border)' : 'none',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }),
    channelAvatar: {
        width: '28px', height: '28px', borderRadius: '6px',
        background: 'linear-gradient(135deg, rgba(0,174,239,0.2), rgba(139,92,246,0.2))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: '10px', fontWeight: 600,
    },

    // Pagination
    pagination: {
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        gap: '12px', marginTop: '14px',
    },
    pageBtn: {
        width: '32px', height: '32px', borderRadius: '8px',
        background: 'var(--input-bg)', border: '1px solid var(--border)',
        color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', cursor: 'pointer', fontFamily: fontStack,
    },

    // Detail panel
    detailOverlay: {
        position: 'fixed', inset: 0, zIndex: 1001,
    },
    detailPanel: {
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '380px', maxWidth: '90vw',
        background: 'var(--modal-bg)', backdropFilter: 'var(--modal-blur)', WebkitBackdropFilter: 'var(--modal-blur)',
        borderLeft: '1px solid var(--border)',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.3)',
        zIndex: 1002, padding: '24px', overflowY: 'auto',
        animation: 'slideInRight 0.25s ease-out',
    },
    detailGrid: {
        borderRadius: '10px', overflow: 'hidden',
        border: '1px solid var(--border)',
    },
    detailRow: (isLast) => ({
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 14px', background: 'var(--input-bg)',
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
    }),

    // Messages
    errorMsg: {
        background: 'var(--error-bg)', border: '1px solid var(--error-border)',
        borderRadius: '10px', padding: '10px 14px', marginBottom: '16px',
        color: 'var(--error)', fontSize: '13px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    },
    successMsg: {
        background: 'var(--success-bg)', border: '1px solid var(--success-border)',
        borderRadius: '10px', padding: '10px 14px', marginBottom: '16px',
        color: 'var(--success)', fontSize: '13px',
    },
};
